import { CoreApi, ErrorMessages } from '@inter-ikea-storageone/range';
import { ComponentIds, Entity, MeasurementUtils } from '@inter-ikea-core/core';
import { BehaviorParameters } from 'rangeConfig/@types';
import { ProductType } from '@inter-ikea-storageone/range';
import { Vector, HierarchyUtils } from '@inter-ikea-core/core';
import { enableDarkModeIfLightsInScene } from 'rangeConfig/shared/utils/darkMode';
import { hideNoFramesInSceneErrorMessage } from 'rangeConfig/shared/rules/hideMenuRules';

const autoFillLightsBehavior = ({
  coreApi,
  rootState: {
    products: { inMarket: products, selectedGroupId },
    layout: { darkScene }
  },
  props
}: BehaviorParameters) => {
  const productId =
    props.coreProductIds && props.coreProductIds.length > 0
      ? props.coreProductIds[0]
      : props.coreProductId;

  const lightProduct = products.find(p => p.coreProductId === productId);
  const isInnerLight =
    (lightProduct && lightProduct.type === ProductType.PAX_INNER_LIGHT) ||
    (lightProduct && lightProduct.type === ProductType.PAX_INNER_LIGHT_OVERSIDAN);

  if (isInnerLight) {
    autoFillInnerLights(productId, coreApi);
  } else {
    autoFillOuterLights(productId, coreApi);
  }
  enableDarkModeIfLightsInScene(coreApi, darkScene, selectedGroupId);
};

const autoFillOuterLights = (productId: string, coreApi: CoreApi) => {
  const selectedEntity = coreApi.entities.findSelectedEntity();

  let frames: Entity[] = [];
  let selectNewLight = false;

  if (selectedEntity) {
    const isOuterLight = coreApi.entities.isProductType(
      selectedEntity,
      ProductType.PAX_OUTER_LIGHT
    );

    if (isOuterLight) {
      const frame = coreApi.entities.getParent(selectedEntity);
      if (frame) {
        frames = [frame];
        selectNewLight = true;
      }
    } else {
      frames = [selectedEntity];
    }
  } else {
    frames = coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID);
  }

  let placementResults = coreApi.products.autoFillOuterLights(productId, frames);

  // Trigger placement result by adding even if no frames to trigger placementAttempt
  if (frames.length === 0) {
    placementResults = [coreApi.products.add(productId)];
  }

  const placementResult = placementResults?.find(result => !result.valid);
  if (placementResult && placementResult.errors && placementResult.errors.length > 0) {
    const placementAttemptErrorMessage = !hideNoFramesInSceneErrorMessage(coreApi)
      ? ErrorMessages.NoFramesInScene
      : ErrorMessages.NoRoomForOuterLights;
    coreApi.emit.onPlacementAttempt(placementAttemptErrorMessage);
  }

  if (selectNewLight && frames.length > 0) {
    const newLights = coreApi.entities.getChildrenWithProductType(
      frames[0],
      ProductType.PAX_OUTER_LIGHT
    );
    if (newLights.length < 1) {
      return;
    }

    coreApi.entities.select(newLights[0]);
  }
};

const autoFillInnerLights = (productId: string, coreApi: CoreApi) => {
  const selectedEntity = coreApi.entities.findSelectedEntity();

  const isInnerLight = (entity: Entity) =>
    coreApi.entities.isOneOfProductTypes(entity, [
      ProductType.PAX_INNER_LIGHT,
      ProductType.PAX_INNER_LIGHT_OVERSIDAN
    ]);

  let oldLights: Entity[] = [];
  let targets: Entity[] = [];

  if (selectedEntity) {
    if (
      coreApi.entities.isOneOfProductTypes(selectedEntity, [
        ProductType.PAX_INNER_LIGHT,
        ProductType.PAX_INNER_LIGHT_OVERSIDAN
      ])
    ) {
      oldLights = [selectedEntity];
    } else {
      oldLights = HierarchyUtils.children(selectedEntity).filter(
        entity =>
          coreApi.entities.isProductType(entity, ProductType.PAX_INNER_LIGHT) ||
          coreApi.entities.isProductType(entity, ProductType.PAX_INNER_LIGHT_OVERSIDAN)
      );
    }

    if (isInnerLight(selectedEntity)) {
      const parent = coreApi.entities.getParent(selectedEntity);

      if (parent) {
        targets.push(parent);
      }
    } else if (
      coreApi.entities.isProductType(selectedEntity, ProductType.PAX_T_DIVIDER) &&
      oldLights.length > 0
    ) {
      oldLights.pop();
    }
    targets.push(selectedEntity);
  } else {
    oldLights = [
      ...coreApi.entities.getByProductTypes([ProductType.PAX_INNER_LIGHT]),
      ...coreApi.entities.getByProductTypes([ProductType.PAX_INNER_LIGHT_OVERSIDAN])
    ];
    const clothRails = coreApi.entities.getByProductTypes([
      ProductType.PAX_CLOTH_RAIL_350,
      ProductType.PAX_CLOTH_RAIL_580
    ]);
    if (clothRails.length > 0) {
      targets = getEntitiesAbove(clothRails, coreApi);
    } else {
      targets = coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID);
    }
  }

  const additionalTargets = coreApi.entities.getByProductType(ProductType.PAX_GLASS_SHELF);

  let placementResults = coreApi.products.autoFillInnerLights(
    productId,
    [...targets, ...additionalTargets],
    oldLights
  );

  // Trigger placement result by adding even if no frames to trigger placementAttempt
  if (targets.length === 0) {
    placementResults = [coreApi.products.add(productId)];
  }

  const placementResult = placementResults?.find(result => !result.valid);
  if (placementResult && placementResult.errors && placementResult.errors.length > 0) {
    if (!hideNoFramesInSceneErrorMessage(coreApi)) {
      coreApi.emit.onPlacementAttempt(ErrorMessages.NoFramesInScene);
    }
  }

  if (selectedEntity && isInnerLight(selectedEntity)) {
    const placedEntity = placementResults[0].placedEntity;

    if (placedEntity) {
      coreApi.entities.select(placedEntity);
    }
  }
};

export const getEntitiesAbove = (targets: Entity[], coreApi: CoreApi) => {
  const entities: Entity[] = [];

  const directionToTop = new Vector(0, 1, 0);

  targets.forEach(target => {
    const targetParent = coreApi.entities.getParent(target);
    if (targetParent) {
      const measurement = MeasurementUtils.getInsideMeasurementLocalSpace(
        target,
        targetParent,
        directionToTop
      );
      if (measurement) {
        entities.push(measurement.end.entity);
      }
    }
  });
  return entities;
};

export default autoFillLightsBehavior;
